/**
 * `/fleet` — Fleet health. The port of the demo's `renderFleet()` (line 2144).
 *
 * Every machine in the population, mine by mine, then division › subclass, with
 * one `MachineCard` each. A jump bar scrolls to a site; a site header carries
 * the band counts and the cost per operating hour and links through to the full
 * site page.
 *
 * ## Data
 *
 * `telemetry` — `daily_metric` (the 12-day key-metric series) and `shift_daily`
 * (availability). Both are columnar and are unpacked once per payload.
 *
 * ## Declared omissions
 *
 * - **Customer org marks.** The demo prints the mine owner's and contractor's
 *   logos beside each site name (`mineMarks(m, 18)`). Those SVGs are part of
 *   the demo's `BRANDJ.svg` set, which the extractor did not split per
 *   organisation, so the site header carries the site's colour swatch instead.
 * - **Turntable thumbnails** on the cards — Tier 3, see `MachineCard`.
 *
 * @see src/versions/v1/modules/fleet/FleetHealthScene.transform.ts
 * @see docs/logic/fleet/fleet-health.md
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Gauge } from 'lucide-react';
import {
  EmptyState,
  HelpAnchor,
  InlineError,
  SkeletonCardGrid,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { AskStrip } from '../../chrome-oem/AskStrip';
import { Disclosure } from '../../shared/Disclosure';
import { useDemoMode } from '../../shared/DemoModeContext';
import { MINE_COL } from '../../shared/oem-engine';
import { unpackTable } from '../../shared/columnar';
import {
  useOemData,
  type DailyMetricRow,
  type ShiftDailyRow,
} from '../../shared/useOemData';
import './palette';
import { BandLegend } from './BandLegend';
import { MachineCard } from './MachineCard';
import {
  fleetBandLegend,
  fleetJumpBar,
  fleetSections,
  type FleetSceneCtx,
} from './FleetHealthScene.transform';

/**
 * The page's standfirst, cut to one sentence (simplification pass B4). What
 * each card charts — the class key metric over 12 days, with its alarm limit
 * — is said by the cards and by the band legend directly under this line, so
 * the demo's four-line version (demo line 2144) was saying it twice.
 */
const STANDFIRST =
  'Every machine in the population, mine by mine, banded by its weakest component.';

/** Demo line 2144. The data-mode chip in the page header. */
const DATA_MODE_NOTE = 'MI / OH / GD live · KG / BR shift-end 09 Sep 06:05';

/** The B5 band's summary count — "6 sites · 100 machines". */
function machineBandCount(sections: ReturnType<typeof fleetSections>): string {
  const machines = sections.reduce((n, section) => n + section.summary.machines.length, 0);
  return `${sections.length} sites · ${machines} machines`;
}

export function FleetHealthScene() {
  const { data, loading, error } = useOemData('telemetry');

  const ctx = useMemo<FleetSceneCtx | null>(() => {
    if (!data) return null;
    return {
      dailyMetric: unpackTable<DailyMetricRow>(data.daily_metric),
      shiftDaily: unpackTable<ShiftDailyRow>(data.shift_daily),
    };
  }, [data]);

  const sections = useMemo(() => (ctx ? fleetSections(ctx) : []), [ctx]);
  const jump = useMemo(() => fleetJumpBar(), []);
  const legend = useMemo(() => fleetBandLegend(), []);

  /**
   * B5. The six per-mine sections are ~100 sparkline cards and took the page
   * to twelve viewports. The executive answer — how many machines are in
   * trouble, and where — is the band legend and the six-site strip above
   * them; the cards are the operational layer, so they sit behind a closed
   * band rather than on the default path. Nothing is deleted, and the jump
   * bar still works: it opens the band before it scrolls.
   */
  const [machinesOpen, setMachinesOpen] = useState(false);

  // Full demo mode restores the source demo's depth: the band starts open,
  // like every uncontrolled Disclosure does in Full. The mode is read from
  // storage after first paint, so follow it rather than seeding useState.
  const { mode: demoMode } = useDemoMode();
  useEffect(() => {
    setMachinesOpen(demoMode === 'full');
  }, [demoMode]);

  /**
   * The site the jump bar last asked for, held until its section exists.
   *
   * Opening the band mounts six sections and ~100 sparkline cards, so the
   * target is not in the document on the click. Scrolling belongs in an
   * effect that runs after the commit that mounted it, not in a frame
   * callback guessing when React will be finished.
   */
  const [pendingAnchor, setPendingAnchor] = useState<
    { anchorId: string; smooth: boolean } | null
  >(null);

  const scrollTo = useCallback(
    (anchorId: string) => {
      // Smooth only when the band is already open. When the click is also
      // opening it, six sections and ~100 sparklines mount in the same
      // commit and the reflow cancels a smooth scroll mid-animation, which
      // leaves the page exactly where it started.
      setPendingAnchor({ anchorId, smooth: machinesOpen });
      setMachinesOpen(true);
    },
    [machinesOpen],
  );

  useEffect(() => {
    if (!pendingAnchor) return;
    const target = document.getElementById(pendingAnchor.anchorId);
    if (!target) return;
    target.scrollIntoView({
      behavior: pendingAnchor.smooth ? 'smooth' : 'auto',
      block: 'start',
    });
    setPendingAnchor(null);
  }, [pendingAnchor, sections]);

  return (
    <SurfacePageLayout width="full">
      <Commentable name="FleetHealthScene">
        <div className="contents">
          <SurfacePageBody className="space-y-8" as="div">
            <SurfacePageHeader
              title="Fleet health"
              description={STANDFIRST}
              actions={
                <div className="flex items-center gap-2">
                  <span className="mono rounded-full bg-zinc-100 px-2.5 py-1 text-[11px] text-muted dark:bg-zinc-800">
                    {DATA_MODE_NOTE}
                  </span>
                  <HelpAnchor doc="fleet/fleet-health" />
                </div>
              }
            />


            <Commentable name="FleetBandLegend">
              <BandLegend
                entries={legend}
                caption="Condition band = the machine's lowest component score."
              />
            </Commentable>

            <Commentable name="FleetJumpBar">
              <nav aria-label="Jump to a site" className="flex flex-wrap gap-2">
                {jump.map((entry) => (
                  <button
                    key={entry.mine}
                    type="button"
                    onClick={() => scrollTo(entry.anchorId)}
                    className="rounded-lg border border-[var(--glass-border)] px-3 py-2 text-left transition-colors hover:bg-[var(--glass-surface-strong)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--color-accent-500)_60%,transparent)]"
                  >
                    <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100">
                      {entry.name}
                    </span>
                    <span className="block text-[11px] text-muted">
                      {entry.machines} machines
                      {entry.toWatch ? (
                        <>
                          {' · '}
                          <b style={{ color: 'var(--band-vpoor)' }}>{entry.toWatch}</b> to watch
                        </>
                      ) : null}
                    </span>
                  </button>
                ))}
              </nav>
            </Commentable>

            {/* fork(oem/v1.8): from "where is the trouble" to "which one first". */}
            <Link
              to="/priorities"
              className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-[var(--glass-border)] px-4 py-3 transition-colors hover:bg-[var(--glass-surface-strong)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--color-accent-500)_60%,transparent)]"
            >
              <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                Which machine first?
              </span>
              <span className="text-xs text-muted">
                The A$ each machine is exposed to, broken down by driver and ranked.
              </span>
              <span className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-accent-700 dark:text-accent-400">
                Priorities
                <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
              </span>
            </Link>

            {/* B3: the ask chips sit under the first row of figures. */}
            <AskStrip view="fleet" />

            {error ? (
              <InlineError>{`Telemetry did not load: ${error.message}`}</InlineError>
            ) : loading || !ctx ? (
              <SkeletonCardGrid count={8} />
            ) : sections.length === 0 ? (
              <EmptyState
                icon={<Gauge className="mx-auto h-8 w-8 text-zinc-400" strokeWidth={1.6} />}
                title="No machines"
                description="The fleet register is empty."
              />
            ) : (
              <Disclosure
                label="Every machine, mine by mine"
                count={machineBandCount(sections)}
                hint="A sparkline card per machine: the class key metric as a daily mean over the last twelve days, with the alarm limit where one applies."
                open={machinesOpen}
                onOpenChange={setMachinesOpen}
              >
                {sections.map((section) => (
                  <section
                    key={section.mine}
                    id={section.anchorId}
                    className="scroll-mt-6 space-y-4"
                  >
                    <header className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-divider pb-2">
                      <span
                        aria-hidden="true"
                        className="inline-block h-3 w-3 rounded-sm"
                        style={{ background: MINE_COL[section.mine] }}
                      />
                      <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
                        {section.name}
                      </h2>
                      <span className="text-xs text-muted">{section.detail}</span>
                      <span
                        className={`band-pill ${section.isLive ? 'band-good' : 'band-fair'}`}
                      >
                        {section.dataMode}
                      </span>
                      <span className="text-xs text-muted">
                        <b>{section.summary.machines.length}</b> machines ·{' '}
                        <b style={{ color: 'var(--band-vpoor)' }}>
                          {section.summary.veryPoorOrWorse}
                        </b>{' '}
                        very poor or worse ·{' '}
                        <b style={{ color: 'var(--band-poor)' }}>{section.summary.poor}</b> poor ·
                        cost <b className="mono">A${section.summary.costPerOpHour}</b>/op h
                      </span>
                      <Link
                        to={`/sites/${section.mine}`}
                        className="ml-auto text-xs text-accent-700 underline underline-offset-2 hover:no-underline dark:text-accent-400"
                      >
                        Site page
                      </Link>
                    </header>

                    {section.groups.map((group) => (
                      <div key={group.key} className="@container space-y-2">
                        <div className="flex items-baseline justify-between gap-3">
                          <p className="text-xs uppercase tracking-wider text-muted">
                            {group.eyebrow}
                          </p>
                          <span className="mono text-xs text-muted">{group.count}</span>
                        </div>
                        <div className="grid grid-cols-1 gap-3 @md:grid-cols-2 @3xl:grid-cols-3 @5xl:grid-cols-4">
                          {group.cards.map((card) => (
                            <MachineCard key={card.machine.machine_id} card={card} />
                          ))}
                        </div>
                      </div>
                    ))}
                  </section>
                ))}
              </Disclosure>
            )}
          </SurfacePageBody>
        </div>
      </Commentable>
    </SurfacePageLayout>
  );
}
